/**
 * Image Board — every screenshot from every note and every trade
 * journal entry, in one grid, newest first.
 *
 * The drawers answer "what did I write about this trade". This
 * answers the other direction: "show me the pictures I took, I'll
 * recognise the one I want". Right-click a tile to jump back to the
 * note or trade it belongs to.
 *
 * Fullscreen rather than a panel inside the Notes drawer: the drawer
 * is 420px, and browsing a few hundred chart screenshots three
 * narrow columns at a time defeats the purpose.
 *
 * Tiles are the full-resolution PNGs — there is no thumbnail
 * endpoint — so every tile image is lazy and fixed-size. If the
 * board ever feels heavy, a server-side `?w=320` variant is the fix,
 * not a smaller grid.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector, useCan } from "@/state/hooks";
import { CAP } from "@/api/capabilities";
import {
  requestJournalFocus,
  requestNoteFocus,
  setImageBoardOpen,
} from "@/state/slices/uiSlice";
import {
  deleteNoteAttachment,
  fetchNotes,
  updateNoteAttachment,
  uploadNoteAttachment,
} from "@/state/slices/notesSlice";
import {
  deleteJournalAttachment,
  fetchJournal,
  updateJournalAttachment,
  uploadJournalAttachment,
} from "@/state/slices/journalSlice";
import { apiUrl } from "@/api/client";
import { fmtShortDateTimeIso } from "@/utils/chartTime";
import { AttachmentLabel } from "@/components/AttachmentLabel/AttachmentLabel";
import { ImageEditor } from "@/components/ImageEditor/ImageEditor";
import {
  ImageLightbox,
  type GalleryImage,
} from "@/components/ImageLightbox/ImageLightbox";
import styles from "./ImageBoard.module.css";

type SourceFilter = "all" | "note" | "trade";

interface BoardImage {
  /** Unique across both sources — the attachment id alone isn't,
   *  since notes and journal mint theirs independently. */
  key: string;
  source: "note" | "trade";
  /** Note id, or journal key (`pos:…` / `pending:…`). */
  ownerId: string;
  /** Human-readable handle for the owner — "#12" for a note, the
   *  ticket for a trade. This is what actually identifies the row a
   *  tile came from: titles are often empty or repeated, and the
   *  note's UUID is unreadable. */
  ownerRef: string;
  ownerTitle: string;
  /** Small muted line under the title — symbol · TF for notes. */
  ownerBadge: string | null;
  attId: string;
  label: string;
  filename: string;
  url: string;
  uploadedAt: string;
}

/** `pos:12345` → `Trade 12345`. Used when a journal entry has no
 *  reason text to show, which is common for a screenshot taken
 *  before the user got round to writing anything. */
function fallbackTradeTitle(key: string): string {
  const [kind, id] = key.split(":");
  if (!id) return key;
  return kind === "pending" ? `Pending ${id}` : `Trade ${id}`;
}

export function ImageBoard() {
  const dispatch = useAppDispatch();
  const open = useAppSelector((s) => s.ui.imageBoardOpen);
  const notes = useAppSelector((s) => s.notes.list);
  const notesStatus = useAppSelector((s) => s.notes.status);
  const journal = useAppSelector((s) => s.journal.byKey);
  const journalStatus = useAppSelector((s) => s.journal.status);
  // Notes are available to every signed-in user; the journal is
  // capability-gated. Without the cap the fetch would just 403, so
  // don't make it and don't offer a filter that can only ever be
  // empty.
  const canJournal = useCan(CAP.journalRead);

  const [source, setSource] = useState<SourceFilter>("all");
  const [query, setQuery] = useState("");
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [menu, setMenu] = useState<{
    x: number;
    y: number;
    image: BoardImage;
  } | null>(null);
  const [editing, setEditing] = useState<BoardImage | null>(null);

  // Both drawers load lazily on their own first open, so the board
  // can be the first thing that needs either set.
  useEffect(() => {
    if (!open) return;
    if (notesStatus === "idle") void dispatch(fetchNotes());
    if (canJournal && journalStatus === "idle") void dispatch(fetchJournal());
  }, [open, notesStatus, journalStatus, canJournal, dispatch]);

  // Escape closes the context menu first, then the board.
  //
  // Capture-phase with stopPropagation, because the drawer that
  // launched the board also closes itself on a bubbling Escape —
  // without this, one Escape would dismiss the board AND the drawer
  // underneath it.
  //
  // It bails while a lightbox or the annotator is open so THEIR
  // Escape handlers get the key. This listener is registered before
  // theirs (the board mounts first), and capture listeners on the
  // same target fire in registration order, so bailing rather than
  // handling is the only thing that keeps the innermost surface in
  // charge.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (lightbox !== null || editing) return;
      e.stopPropagation();
      if (menu) setMenu(null);
      else dispatch(setImageBoardOpen(false));
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, menu, lightbox, editing, dispatch]);

  const all = useMemo<BoardImage[]>(() => {
    const out: BoardImage[] = [];
    for (const note of notes) {
      note.attachments.forEach((att, i) => {
        out.push({
          key: `note:${note.id}:${att.id}`,
          source: "note",
          ownerId: note.id,
          ownerRef: note.seq != null ? `#${note.seq}` : "#?",
          ownerTitle: note.title || "(untitled)",
          ownerBadge: note.symbol_context
            ? `${note.symbol_context}${note.timeframe_context ? ` · ${note.timeframe_context}` : ""}`
            : null,
          attId: att.id,
          label: att.label || String(i + 1),
          filename: att.filename,
          url: apiUrl(
            `/api/notes/${encodeURIComponent(note.id)}/attachments/${encodeURIComponent(att.id)}`,
          ),
          uploadedAt: att.uploaded_at,
        });
      });
    }
    for (const entry of Object.values(journal)) {
      entry.attachments.forEach((att, i) => {
        out.push({
          key: `trade:${entry.key}:${att.id}`,
          source: "trade",
          ownerId: entry.key,
          ownerRef: fallbackTradeTitle(entry.key),
          ownerTitle:
            firstLine(entry.reason) || fallbackTradeTitle(entry.key),
          // Nothing extra to add for a trade: the ticket is already
          // the ref, and repeating it in the meta line is noise.
          ownerBadge: null,
          attId: att.id,
          label: att.label || String(i + 1),
          filename: att.filename,
          url: apiUrl(`/api/journal/attachments/${att.id}`),
          uploadedAt: att.uploaded_at,
        });
      });
    }
    // Newest first. ISO-8601 UTC strings sort lexicographically, so
    // no Date parsing per comparison.
    out.sort((a, b) => (a.uploadedAt < b.uploadedAt ? 1 : -1));
    return out;
  }, [notes, journal]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter((img) => {
      if (source !== "all" && img.source !== source) return false;
      if (!q) return true;
      return (
        img.ownerTitle.toLowerCase().includes(q) ||
        img.ownerRef.toLowerCase().includes(q) ||
        img.label.toLowerCase().includes(q) ||
        img.filename.toLowerCase().includes(q) ||
        (img.ownerBadge ?? "").toLowerCase().includes(q)
      );
    });
  }, [all, source, query]);

  // Day buckets, in the already-sorted order. Scanning a few hundred
  // screenshots is much easier with a date to anchor against.
  const groups = useMemo(() => {
    const out: { day: string; images: BoardImage[] }[] = [];
    for (const img of filtered) {
      const day = img.uploadedAt.slice(0, 10);
      const last = out[out.length - 1];
      if (last && last.day === day) last.images.push(img);
      else out.push({ day, images: [img] });
    }
    return out;
  }, [filtered]);

  const gallery = useMemo<GalleryImage[]>(
    () =>
      filtered.map((img) => ({
        id: img.key,
        url: img.url,
        label: img.label,
        filename: img.filename,
        context: `${img.ownerRef} · ${img.ownerTitle}`,
      })),
    [filtered],
  );

  const rename = (img: BoardImage, next: string) => {
    if (img.source === "note") {
      void dispatch(
        updateNoteAttachment({
          id: img.ownerId,
          attId: img.attId,
          patch: { label: next },
        }),
      );
    } else {
      void dispatch(
        updateJournalAttachment({
          key: img.ownerId,
          attId: img.attId,
          patch: { label: next },
        }),
      );
    }
  };

  const remove = (img: BoardImage) => {
    if (
      !window.confirm(
        `Delete image #${img.label} from ${img.ownerRef} "${img.ownerTitle}"? This can't be undone.`,
      )
    ) {
      return;
    }
    if (img.source === "note") {
      void dispatch(
        deleteNoteAttachment({ id: img.ownerId, attId: img.attId }),
      );
    } else {
      void dispatch(
        deleteJournalAttachment({ key: img.ownerId, attId: img.attId }),
      );
    }
  };

  const jump = (img: BoardImage) => {
    if (img.source === "note") dispatch(requestNoteFocus(img.ownerId));
    else dispatch(requestJournalFocus(img.ownerId));
  };

  /** Same upload-then-delete order as the drawers, carrying the
   *  label over so the number the notes cite doesn't move. */
  const saveEdited = async (blob: Blob, filename: string) => {
    if (!editing) return;
    const img = editing;
    if (img.source === "note") {
      await dispatch(
        uploadNoteAttachment({
          id: img.ownerId,
          blob,
          filename,
          label: img.label,
        }),
      ).unwrap();
      await dispatch(
        deleteNoteAttachment({ id: img.ownerId, attId: img.attId }),
      ).unwrap();
    } else {
      await dispatch(
        uploadJournalAttachment({
          key: img.ownerId,
          blob,
          filename,
          label: img.label,
        }),
      ).unwrap();
      await dispatch(
        deleteJournalAttachment({ key: img.ownerId, attId: img.attId }),
      ).unwrap();
    }
    setEditing(null);
  };

  if (!open) return null;

  const loading =
    (notesStatus === "loading" ||
      (canJournal && journalStatus === "loading")) &&
    all.length === 0;

  return (
    <div className={styles.overlay} onClick={() => setMenu(null)}>
      <div className={styles.header}>
        <div className={styles.title}>Images</div>
        <div className={styles.chips}>
          {(
            canJournal
              ? ([
                  ["all", "All"],
                  ["note", "Notes"],
                  ["trade", "Trades"],
                ] as [SourceFilter, string][])
              : ([["all", "All"]] as [SourceFilter, string][])
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={`${styles.chip} ${source === id ? styles.chipActive : ""}`}
              onClick={() => setSource(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <input
          className={styles.search}
          placeholder="Filter by note title, image name, symbol…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <span className={styles.count}>
          {filtered.length} image{filtered.length === 1 ? "" : "s"}
        </span>
        <button
          type="button"
          className={styles.close}
          onClick={() => dispatch(setImageBoardOpen(false))}
          title="Close (Esc)"
          aria-label="Close image board"
        >
          ×
        </button>
      </div>

      <div className={styles.body}>
        {loading ? (
          <div className={styles.empty}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div className={styles.empty}>
            {all.length === 0
              ? "No images yet. Screenshots attached to a note or a trade show up here."
              : "Nothing matches that filter."}
          </div>
        ) : (
          groups.map((group) => (
            <section key={group.day} className={styles.group}>
              <h3 className={styles.groupDay}>{formatDay(group.day)}</h3>
              <div className={styles.grid}>
                {group.images.map((img) => (
                  <Tile
                    key={img.key}
                    image={img}
                    onOpen={() =>
                      setLightbox(filtered.findIndex((f) => f.key === img.key))
                    }
                    onRename={(v) => rename(img, v)}
                    onMenu={(x, y) => setMenu({ x, y, image: img })}
                  />
                ))}
              </div>
            </section>
          ))
        )}
      </div>

      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          image={menu.image}
          onClose={() => setMenu(null)}
          onJump={() => jump(menu.image)}
          onOpen={() =>
            setLightbox(filtered.findIndex((f) => f.key === menu.image.key))
          }
          onAnnotate={() => setEditing(menu.image)}
          onDelete={() => remove(menu.image)}
        />
      )}

      {editing && (
        <ImageEditor
          src={editing.url}
          filename={editing.filename}
          onCancel={() => setEditing(null)}
          onSave={saveEdited}
        />
      )}

      {lightbox !== null && (
        <ImageLightbox
          items={gallery}
          startIndex={lightbox}
          onClose={() => setLightbox(null)}
          onEdit={(item) => {
            const img = filtered.find((f) => f.key === item.id);
            if (!img) return;
            setLightbox(null);
            setEditing(img);
          }}
          onDelete={(item) => {
            const img = filtered.find((f) => f.key === item.id);
            if (img) remove(img);
          }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------

function Tile({
  image,
  onOpen,
  onRename,
  onMenu,
}: {
  image: BoardImage;
  onOpen: () => void;
  onRename: (next: string) => void;
  onMenu: (x: number, y: number) => void;
}) {
  return (
    <figure
      className={styles.tile}
      onClick={onOpen}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onMenu(e.clientX, e.clientY);
      }}
      title={`${image.filename} — right-click for actions`}
    >
      <div className={styles.thumb}>
        <img
          src={image.url}
          alt={image.filename}
          loading="lazy"
          decoding="async"
        />
        <AttachmentLabel
          label={image.label}
          onRename={onRename}
          compact
          hint={`Image #${image.label} — click to rename`}
        />
        <span
          className={`${styles.sourceDot} ${image.source === "note" ? styles.dotNote : styles.dotTrade}`}
          title={image.source === "note" ? "From a note" : "From a trade"}
        />
      </div>
      <figcaption className={styles.caption}>
        <span className={styles.captionTitle}>
          <span className={styles.captionRef}>{image.ownerRef}</span>
          {image.ownerTitle}
        </span>
        <span className={styles.captionMeta}>
          {image.ownerBadge ? `${image.ownerBadge} · ` : ""}
          {fmtShortDateTimeIso(image.uploadedAt)}
        </span>
      </figcaption>
    </figure>
  );
}

// -----------------------------------------------------------------

function ContextMenu({
  x,
  y,
  image,
  onClose,
  onJump,
  onOpen,
  onAnnotate,
  onDelete,
}: {
  x: number;
  y: number;
  image: BoardImage;
  onClose: () => void;
  onJump: () => void;
  onOpen: () => void;
  onAnnotate: () => void;
  onDelete: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Nudge back on-screen when opened near the right or bottom edge —
  // a menu that opens half off the viewport can't be clicked.
  const [pos, setPos] = useState({ x, y });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({
      x: Math.min(x, window.innerWidth - rect.width - 8),
      y: Math.min(y, window.innerHeight - rect.height - 8),
    });
  }, [x, y]);

  const run = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
    fn();
  };

  return (
    <div
      ref={ref}
      className={styles.menu}
      style={{ left: pos.x, top: pos.y }}
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
      role="menu"
    >
      <button type="button" className={styles.menuItem} onClick={run(onJump)}>
        {image.source === "note" ? "Jump to note" : "Jump to trade"}
      </button>
      <button type="button" className={styles.menuItem} onClick={run(onOpen)}>
        Open
      </button>
      <button
        type="button"
        className={styles.menuItem}
        onClick={run(onAnnotate)}
      >
        Annotate
      </button>
      <div className={styles.menuSep} />
      <button
        type="button"
        className={`${styles.menuItem} ${styles.menuDanger}`}
        onClick={run(onDelete)}
      >
        Delete image
      </button>
    </div>
  );
}

/** First line of a multi-line field, trimmed. */
function firstLine(text: string): string {
  const idx = text.indexOf("\n");
  return (idx === -1 ? text : text.slice(0, idx)).trim();
}

/** `2026-09-25` → `Today` / `Yesterday` / `Sep 25, 2026`. Compared
 *  against the chart display zone (UTC), same as every other
 *  timestamp in the app — see chartTime.ts. */
function formatDay(day: string): string {
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86_400_000)
    .toISOString()
    .slice(0, 10);
  if (day === today) return "Today";
  if (day === yesterday) return "Yesterday";
  const d = new Date(`${day}T00:00:00Z`);
  return d.toLocaleDateString(undefined, {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
